import { css } from '@emotion/react'
import { graphql, useStaticQuery } from 'gatsby'
import { ComponentPropsWithoutRef } from 'react'

import { Seal } from '@/features/brearley-logo'
import { DatoStructuredText } from '@/features/common/'
import { GatsbyImageFocused } from '@/features/common/'
import { DatoLink } from '@/features/dato-link'
import { absoluteFill, aspectRatio, baseGrid, mq } from '@/theme/mixins'
import { colors } from '@/theme/variables'

export const HomeAboutSection = ({
  ...props
}: ComponentPropsWithoutRef<'section'>) => {
  const { datoCmsHomePage } =
    useStaticQuery<Queries.HomeAboutSectionQuery>(graphql`
      query HomeAboutSection {
        datoCmsHomePage {
          aboutHeading
          aboutBody {
            value
          }
          aboutLinks {
            ...PageLink
          }
          aboutImage {
            gatsbyImageData(
              width: 960
              sizes: "(max-width: 720px) 75vw, 60vw"
              imgixParams: {
                ar: "1:1"
                fit: "crop"
                crop: "focalpoint"
              }
            )
            ...ImageFocalData
          }
        }
      }
    `)
  const { aboutHeading, aboutBody, aboutLinks, aboutImage } =
    datoCmsHomePage || {}
  const styles = {
    section: css`
      background: #000;
      ${baseGrid};
      margin-top: var(--row-108);
      ${mq().ms} {
        margin-top: 0;
        z-index: 2;
      }
    `,
    text: css`
      position: relative;
      color: #fff;
      margin: var(--row-108) 0 calc(2 * var(--row-108));
      grid-column: 2 / span 5;
      max-width: 48ch;
      align-self: center;
      justify-self: center;
      h2 {
        font-size: var(--fs-60);
        border-bottom: 2px solid ${colors.red};
        margin: 0 0 0.5em;
        padding-bottom: 0.5em;
      }
      ${mq().ms} {
        grid-column: 2 / -2;
        /* margin: var(--row-108) 0 calc(-1 * var(--row-108) + var(--row-36)); */
        margin: var(--row-108) 0 0;
        max-width: 100%;
        p {
          max-width: 60ch;
        }
      }
    `,
    links: css`
      margin-top: 1.75em;
      a {
        @media (hover: hover) {
          &:hover {
            color: ${colors.redLight};
          }
        }
      }
    `,
    image: css`
      grid-column: span 8 / -1;
      grid-row: 1 / 2;
      ${absoluteFill};
      transform: translateY(calc(-1 * var(--row-72)));
      ${mq().ms} {
        position: relative;
        grid-column: span 11 / -1;
        ${aspectRatio(1.5)}
        grid-row: 2 / 3;
        transform: translateY(var(--row-72));
      }
    `,
    seal: css`
      position: absolute;
      opacity: 0.1;
      width: 50vmax;
      height: auto;
      left: -12.5vmax;
      top: -17.5vmax;
      ${mq().ms} {
        width: 100vw;
        top: auto;
        bottom: var(--row-72);
        left: -33vw;
      }
    `,
  }
  return (
    <section
      css={styles.section}
      {...props}
    >
      <Seal
        color="#fff"
        css={styles.seal}
      />
      <div css={styles.text}>
        <h2>{aboutHeading}</h2>
        <DatoStructuredText data={aboutBody} />
        <div css={styles.links}>
          {aboutLinks?.map((link, i) => (
            <DatoLink
              data={link}
              key={i}
              showIcon
            />
          ))}
        </div>
      </div>
      <GatsbyImageFocused
        image={aboutImage?.gatsbyImageData}
        aspectRatio={1}
        css={styles.image}
        data={aboutImage}
      />
    </section>
  )
}
